import { useMemo, useState } from "react";
import { scaleLinear, scaleBand } from "d3-scale";
import { max } from "d3-array";
import { useResize } from "./useResize";
import { ChartTooltip } from "./Tooltip";
import { CHART_TOKENS, seriesColor, fmtInt } from "./theme";
import { useTranslation } from "../../i18n/LocaleProvider";

// Horizontal or vertical bar chart. Designed for categorical data — label
// distributions, leaderboards, project counts. A single data point is
// `{label, value, color?}` — per-bar color overrides fall back to the series
// palette keyed by index so everything stays coherent when colours aren't
// supplied.
export const BarChart = ({
  data = [],
  height,
  orientation = "horizontal",
  valueFormatter = fmtInt,
  maxBars = 12,
  cornerRadius = 4,
  color,
  labelWidth = 140,
}) => {
  const { t } = useTranslation();
  const vertical = orientation === "vertical";
  const trimmed = useMemo(() => data.slice(0, maxBars), [data, maxBars]);
  const derivedHeight = height ?? (vertical ? 220 : Math.max(80, trimmed.length * 28 + 20));
  const [ref, { width }] = useResize(320, derivedHeight);
  const [hover, setHover] = useState(null);

  // ``tiltLabels`` toggles 45° tick labels when the chart is narrow
  // OR carries many buckets. Chart widths reported here are the
  // chart's own container — inside the ProjectMetrics modal's 2-col
  // grid this is often much smaller than the viewport, so a generous
  // threshold is correct. We also tilt any bar chart with >=6
  // buckets because "0-10% / 10-20% / 20-30% …"-style labels overlap
  // before the stride computation would cull them.
  const tiltLabels = vertical && width > 0 && (width < 700 || trimmed.length >= 6);
  const PAD = vertical
    ? { top: 16, right: 12, bottom: tiltLabels ? 52 : 32, left: 40 }
    : { top: 8, right: 40, bottom: 8, left: labelWidth };
  const plotW = Math.max(10, width - PAD.left - PAD.right);
  const plotH = Math.max(10, derivedHeight - PAD.top - PAD.bottom);

  const vmax = max(trimmed, (d) => d.value) ?? 1;

  const { valScale, catScale } = useMemo(() => {
    if (trimmed.length === 0) return { valScale: null, catScale: null };
    const vs = scaleLinear()
      .domain([0, vmax * 1.05 || 1])
      .nice()
      .range(vertical ? [plotH, 0] : [0, plotW]);
    const cs = scaleBand()
      .domain(trimmed.map((d) => d.label))
      .range(vertical ? [0, plotW] : [0, plotH])
      .padding(0.25);
    return { valScale: vs, catScale: cs };
  }, [trimmed, plotW, plotH, vertical, vmax]);

  // Dense categorical axes (30 daily buckets, bucketed histograms,
  // etc.) overlap their per-bar labels. Compute a stride so only as
  // many labels render as will fit with healthy breathing room, and
  // always keep the first and last so the range stays anchored.
  // Widths budgeted: ~48px per tilted label, ~80px per horizontal one.
  const labelStride = useMemo(() => {
    if (!vertical || trimmed.length <= 1) return 1;
    const perLabel = tiltLabels ? 48 : 80;
    const maxLabels = Math.max(2, Math.floor(plotW / perLabel));
    return Math.max(1, Math.ceil(trimmed.length / maxLabels));
  }, [trimmed.length, plotW, vertical, tiltLabels]);

  const showCatLabel = (i) =>
    !vertical || labelStride === 1 || i === 0 || i === trimmed.length - 1 || i % labelStride === 0;

  const isEmpty = trimmed.length === 0;

  return (
    <div ref={ref} style={{ position: "relative", width: "100%", height: derivedHeight }}>
      {isEmpty ? (
        <div style={emptyStyle}>{t("components.charts.bar_chart.no_data")}</div>
      ) : (
        <svg width={width} height={derivedHeight}>
          <g transform={`translate(${PAD.left},${PAD.top})`}>
            {/* gridlines along value axis */}
            {valScale.ticks(4).map((t) => (
              <g key={t}>
                {vertical ? (
                  <line x1={0} x2={plotW} y1={valScale(t)} y2={valScale(t)} stroke={CHART_TOKENS.grid} />
                ) : (
                  <line y1={0} y2={plotH} x1={valScale(t)} x2={valScale(t)} stroke={CHART_TOKENS.grid} />
                )}
                {vertical ? (
                  <text
                    x={-8}
                    y={valScale(t)}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fontSize={11}
                    fill={CHART_TOKENS.label}
                  >
                    {valueFormatter(t)}
                  </text>
                ) : (
                  <text x={valScale(t)} y={plotH + 16} textAnchor="middle" fontSize={11} fill={CHART_TOKENS.label}>
                    {valueFormatter(t)}
                  </text>
                )}
              </g>
            ))}

            {trimmed.map((d, i) => {
              const c = d.color || color || seriesColor(i);
              const x = vertical ? catScale(d.label) : 0;
              const y = vertical ? valScale(d.value) : catScale(d.label);
              const w = vertical ? catScale.bandwidth() : valScale(d.value);
              const h = vertical ? plotH - valScale(d.value) : catScale.bandwidth();
              return (
                <g
                  key={d.label}
                  onMouseEnter={(e) => setHover({ d, x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY })}
                  onMouseMove={(e) => setHover({ d, x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY })}
                  onMouseLeave={() => setHover(null)}
                  style={{ cursor: "default" }}
                >
                  <rect
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    rx={cornerRadius}
                    ry={cornerRadius}
                    fill={c}
                    style={{ transition: "opacity 120ms", opacity: hover && hover.d !== d ? 0.45 : 1 }}
                  />
                  {/* label + value on the horizontal variant for readability */}
                  {!vertical && (
                    <>
                      <text
                        x={-10}
                        y={y + h / 2}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fontSize={12}
                        fill={CHART_TOKENS.labelStrong}
                      >
                        {d.label}
                      </text>
                      <text
                        x={w + 8}
                        y={y + h / 2}
                        dominantBaseline="middle"
                        fontSize={12}
                        fontWeight={500}
                        fill={CHART_TOKENS.labelStrong}
                      >
                        {valueFormatter(d.value)}
                      </text>
                    </>
                  )}
                  {vertical &&
                    showCatLabel(i) &&
                    (tiltLabels ? (
                      // On narrow plots, rotate 45° so wide labels
                      // (e.g. "90-100%", "Apr 23") don't collide with
                      // each other. Anchor the text at its end so the
                      // rotation pivots from the tick point.
                      <text
                        x={x + w / 2}
                        y={plotH + 14}
                        textAnchor="end"
                        fontSize={11}
                        fill={CHART_TOKENS.label}
                        transform={`rotate(-45 ${x + w / 2} ${plotH + 14})`}
                      >
                        {d.label}
                      </text>
                    ) : (
                      <text x={x + w / 2} y={plotH + 16} textAnchor="middle" fontSize={11} fill={CHART_TOKENS.label}>
                        {d.label}
                      </text>
                    ))}
                </g>
              );
            })}
          </g>
        </svg>
      )}

      <ChartTooltip visible={!!hover} x={hover?.x ?? 0} y={hover?.y ?? 0}>
        {hover && (
          <>
            <div style={{ opacity: 0.85 }}>{hover.d.label}</div>
            <div style={{ fontWeight: 600 }}>{valueFormatter(hover.d.value)}</div>
          </>
        )}
      </ChartTooltip>
    </div>
  );
};

const emptyStyle = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: CHART_TOKENS.label,
  fontSize: "var(--font-size-label)",
};
